import { Box, Card, CardContent, Typography, Grid, Chip, CircularProgress, Paper } from '@mui/material'
import Person from '@mui/icons-material/Person'
import Groups from '@mui/icons-material/Groups'
import { styled } from '@mui/material/styles'

const RoleCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  borderRadius: theme.spacing(2.5),
  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.95) 0%, rgba(79, 70, 229, 0.95) 50%, rgba(67, 56, 202, 0.95) 100%)',
  backdropFilter: 'blur(10px) saturate(180%)',
  WebkitBackdropFilter: 'blur(10px) saturate(180%)',
  border: '2px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 4px 16px rgba(67, 56, 202, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.04) 100%)',
    pointerEvents: 'none',
    borderRadius: theme.spacing(2.5),
  },
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 12px 32px rgba(67, 56, 202, 0.25), 0 4px 16px rgba(0, 0, 0, 0.15)',
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
}))

const RoleIcon = styled(Box)(({ theme }) => ({
  width: 72,
  height: 72,
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.25)',
  backdropFilter: 'blur(10px)',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  marginBottom: theme.spacing(2),
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
}))

export default function RoleSelection({ product, roles, onRoleSelect, loading = false }) {
  return (
    <Box>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Groups
          sx={{
            fontSize: { xs: 40, sm: 48, md: 56 },
            color: 'primary.main',
            mb: 2,
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
          }}
        />
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: 'text.primary',
            mb: 1,
          }}
        >
          Select a Role
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: 1.6,
          }}
        >
          Choose a role to manage its specific documentation content for{' '}
          <strong>{product.title || product.packageId}</strong>
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {roles.map((role) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={role.id}>
              <RoleCard onClick={() => onRoleSelect(role)}>
                <CardContent
                  sx={{
                    flex: 1,
                    textAlign: 'center',
                    p: 3,
                    position: 'relative',
                    zIndex: 1,
                    color: 'white',
                  }}
                >
                  <RoleIcon>
                    <Person sx={{ fontSize: 36, color: 'white' }} />
                  </RoleIcon>

                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      mb: 1.5,
                      textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      color: 'inherit',
                    }}
                  >
                    {role.title || role.name || role.id}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: 1.6,
                      mb: 2,
                      opacity: 0.9,
                      color: 'inherit',
                      minHeight: '40px',
                    }}
                  >
                    {role.description || 'No description available'}
                  </Typography>

                  <Chip
                    label={role.id}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                    }}
                  />
                </CardContent>
              </RoleCard>
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && roles.length === 0 && (
        <Paper
          sx={{
            textAlign: 'center',
            py: 8,
            px: 3,
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Groups sx={{ fontSize: 64, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No roles available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This product doesn&apos;t have any configured roles for content management.
          </Typography>
        </Paper>
      )}
    </Box>
  )
} 